import React, { useEffect, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStaticOptions } from "../context/StaticOptionsContext";
import Modal from "../components/modals/Modal";
import { IconExclamationMark } from "@tabler/icons-react";

const CipoTipus = () => {
    const { order, updateOrder } = useOrder();
    const { staticOptions, loading: optionsLoading, error } = useStaticOptions();
    const navigate = useStepNavigate();
    const [loading, setLoading] = useState(false);

    const aktVeny = order.venyek[order.aktivVenyIndex] || {};

    const parseFlag = (value, fallback = 0) => {
        const parsed = Number(value ?? fallback);
        return Number.isNaN(parsed) ? fallback : parsed;
    };

    const onefoot = parseFlag(aktVeny.is_onefoot_meas, order.hmeliorder?.hmeliorder_is_onefoot_meas ?? 0);
    const isLeft = parseFlag(aktVeny.is_left, order.hmeliorder?.hmeliorder_is_left ?? 0);

    const globalLeft = order.hmeliorder.hmeliorder_shoes_med_left || "-";
    const globalRight = order.hmeliorder.hmeliorder_shoes_med_right || "-";

    const [selectedLeft, setSelectedLeft] = useState(
        aktVeny.veny_shoes_med_left || order.hmeliorder.hmeliorder_shoes_med_left || "-"
    );
    const [selectedRight, setSelectedRight] = useState(
        aktVeny.veny_shoes_med_right || order.hmeliorder.hmeliorder_shoes_med_right || "-"
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);

    const hasRealSelection = (value) => value && value !== "-";

    useEffect(() => {
        if (order.hmeliorder?.hmeliorder_order_type === 1 && aktVeny.depot_prefix === "BETÉT") {
            navigate("/BetetTartozek");
        }
    }, [order.hmeliorder?.hmeliorder_order_type, aktVeny.depot_prefix, navigate]);

    useEffect(() => {
        if (!aktVeny.veny_shoes_med_left && globalLeft) {
            setSelectedLeft(globalLeft);
        }
        if (!aktVeny.veny_shoes_med_right && globalRight) {
            setSelectedRight(globalRight);
        }
    }, [aktVeny, globalLeft, globalRight]);

    useEffect(() => {
        if (aktVeny.veny_shoes_med_left) {
            setSelectedLeft(aktVeny.veny_shoes_med_left);
        }
        if (aktVeny.veny_shoes_med_right) {
            setSelectedRight(aktVeny.veny_shoes_med_right);
        }
    }, [aktVeny]);

    useEffect(() => {
        if (order.aktivVenyIndex !== 1) return;
        const first = order.venyek?.[0];
        if (!first) return;

        const firstLeft = first.veny_shoes_med_left;
        const firstRight = first.veny_shoes_med_right;

        const currOnefoot = aktVeny.is_onefoot_meas;
        const currSide = aktVeny.is_left;

        if (currOnefoot === 1) {
            if (currSide === 1 && firstLeft && !selectedRight && !aktVeny.veny_shoes_med_right) {
                setSelectedRight("C0");
                updateOrder((prev) => ({
                    ...prev,
                    venyek: prev.venyek.map((v, i) =>
                        i === prev.aktivVenyIndex
                            ? { ...v, veny_shoes_med_right: "C0" }
                            : v
                    ),
                }));
            }
            if (currSide === 0 && firstRight && !selectedLeft && !aktVeny.veny_shoes_med_left) {
                setSelectedLeft("C0");
                updateOrder((prev) => ({
                    ...prev,
                    venyek: prev.venyek.map((v, i) =>
                        i === prev.aktivVenyIndex
                            ? { ...v, veny_shoes_med_left: "C0" }
                            : v
                    ),
                }));
            }
        }

        if (currOnefoot === 0) {
            if (firstLeft && !firstRight && !selectedRight && !aktVeny.veny_shoes_med_right) {
                setSelectedRight("C0");
                updateOrder((prev) => ({
                    ...prev,
                    venyek: prev.venyek.map((v, i) =>
                        i === prev.aktivVenyIndex
                            ? { ...v, veny_shoes_med_right: "C0" }
                            : v
                    ),
                }));
            }
            if (firstRight && !firstLeft && !selectedLeft && !aktVeny.veny_shoes_med_left) {
                setSelectedLeft("C0");
                updateOrder((prev) => ({
                    ...prev,
                    venyek: prev.venyek.map((v, i) =>
                        i === prev.aktivVenyIndex
                            ? { ...v, veny_shoes_med_left: "C0" }
                            : v
                    ),
                }));
            }
        }
    }, [
        order.aktivVenyIndex,
        aktVeny.is_onefoot_meas,
        aktVeny.is_left,
        aktVeny.veny_shoes_med_left,
        aktVeny.veny_shoes_med_right,
        selectedLeft,
        selectedRight,
        updateOrder,
    ]);

    const handleSelection = (side, value) => {
        if (onefoot === 0) {
            if (selectedLeft === value && selectedRight === value) {
                return;
            }

            if (hasRealSelection(selectedLeft) || hasRealSelection(selectedRight)) {
                setPendingSelection({ side: "both", value });
                setIsModalOpen(true);
                return;
            }

            setSelectedLeft(value);
            setSelectedRight(value);
            return;
        }

        if (side === "left" && selectedLeft === value) {
            return;
        }

        if (side === "right" && selectedRight === value) {
            return;
        }

        if ((side === "left" && hasRealSelection(selectedLeft)) || (side === "right" && hasRealSelection(selectedRight))) {
            setPendingSelection({ side, value });
            setIsModalOpen(true);
            return;
        }

        if (side === "left") {
            setSelectedLeft(value);
        } else if (side === "right") {
            setSelectedRight(value);
        }
    };

    const confirmSelection = () => {
        if (pendingSelection) {
            const { side, value } = pendingSelection;

            if (onefoot === 0) {
                setSelectedLeft(value);
                setSelectedRight(value);
            } else {
                if (side === "left") {
                    setSelectedLeft(value);
                }

                if (side === "right") {
                    setSelectedRight(value);
                }
            }

            setPendingSelection(null);
        }
        setIsModalOpen(false);
    };

    const cancelSelection = () => {
        setPendingSelection(null);
        setIsModalOpen(false);
    };

    const canProceed =
        (onefoot === 0 && selectedLeft !== "-" && selectedRight !== "-") ||
        (onefoot === 1 && isLeft === 1 && selectedLeft !== "-") ||
        (onefoot === 1 && isLeft === 0 && selectedRight !== "-");

    const isLeftDisabled = order.hmeliorder.hmeliorder_shoes_med_left !== "" && order.hmeliorder.hmeliorder_shoes_med_left !== "-";
    const isRightDisabled = order.hmeliorder.hmeliorder_shoes_med_right !== "" && order.hmeliorder.hmeliorder_shoes_med_right !== "-";

    const handleNext = async () => {
        if (!canProceed) {
            return;
        }

        const updatedOrder = {
            ...order,
            venyek: order.venyek.map((veny, index) => {
                if (index === order.aktivVenyIndex) {
                    return {
                        ...veny,
                        veny_shoes_med_left: selectedLeft || "-",
                        veny_shoes_med_right: selectedRight || "-",
                    };
                }
                return veny;
            }),
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_shoes_med_left: selectedLeft || "-",
                hmeliorder_shoes_med_right: selectedRight || "-",
            },
        };

        updateOrder(updatedOrder);

        if (order.hmeliorder?.hmeliorder_order_type === 1 && aktVeny.depot_prefix !== "BETÉT") {
            navigate("/VenyesTartozek");
        } else if (aktVeny.depot_prefix === "BETÉT") {
            navigate("/BetetTipus");
        }
    };

    const handleBackClick = () => {
        navigate("/Mennyiseg");
    };

    return (
        <TablerLayout title="Válassza ki a cipő típusát">
            <div className="row justify-content-center">
                <div className="col-md-10 mb-4">
                    <div className="card-body text-center">
                        <div className="d-flex flex-column flex-md-row justify-content-center gap-4">
                            {/* Bal és Jobb láb oszlopok */}
                            {onefoot === 1 && (
                                <>
                                    {/* Bal láb oszlop */}
                                    <div>
                                        <div
                                            className={
                                                "fw-bold mb-2" + (isLeft === 0 ? " text-secondary" : "")
                                            }
                                            style={{ fontSize: "2rem" }}
                                        >
                                            Bal láb
                                        </div>
                                        <div className="d-flex flex-wrap gap-3 justify-content-center">
                                            {staticOptions?.shoes_med_types?.map((tipus) => (
                                                <button
                                                    key={"bal-" + tipus.aidfinance_code}
                                                    type="button"
                                                    className={`btn w-100 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold ${
                                                        selectedLeft === tipus.aidfinance_code
                                                            ? "btn-primary selected_button"
                                                            : "btn-outline-primary"
                                                    }`}
                                                    style={{ minWidth: 120, fontSize: 22 }}
                                                    onClick={() => handleSelection("left", tipus.aidfinance_code)}
                                                    disabled={isLeft === 0 || isLeftDisabled}
                                                >
                                                    {tipus.aidfinance_code}
                                                    {tipus.aidfinance_national && (
                                                        <span className="mt-1 text-secondary fs-6">
                                                            {tipus.aidfinance_national}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Jobb láb oszlop */}
                                    <div>
                                        <div
                                            className={
                                                "fw-bold mb-2" + (isLeft === 1 ? " text-secondary" : "")
                                            }
                                            style={{ fontSize: "2rem" }}
                                        >
                                            Jobb láb
                                        </div>
                                        <div className="d-flex flex-wrap gap-3 justify-content-center">
                                            {staticOptions?.shoes_med_types?.map((tipus) => (
                                                <button
                                                    key={"jobb-" + tipus.aidfinance_code}
                                                    type="button"
                                                    className={`btn w-100 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold ${
                                                        selectedRight === tipus.aidfinance_code
                                                            ? "btn-primary selected_button"
                                                            : "btn-outline-primary"
                                                    }`}
                                                    style={{ minWidth: 120, fontSize: 22 }}
                                                    onClick={() => handleSelection("right", tipus.aidfinance_code)}
                                                    disabled={isLeft === 1 || isRightDisabled}
                                                >
                                                    {tipus.aidfinance_code}
                                                    {tipus.aidfinance_national && (
                                                        <span className="mt-1 text-secondary fs-6">
                                                            {tipus.aidfinance_national}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Mindkettő oszlop */}
                            {onefoot === 0 && (
                                <>
                                    {/* Bal láb oszlop */}
                                    <div>
                                        <div className="fw-bold mb-2" style={{ fontSize: "2rem" }}>
                                            Bal láb
                                        </div>
                                        <div className="d-flex flex-wrap gap-3 justify-content-center">
                                            {staticOptions?.shoes_med_types?.map((tipus) => (
                                                <button
                                                    key={"bal-" + tipus.aidfinance_code}
                                                    type="button"
                                                    className={`btn w-100 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold ${
                                                        selectedLeft === tipus.aidfinance_code
                                                            ? "btn-primary selected_button"
                                                            : "btn-outline-primary"
                                                    }`}
                                                    style={{ minWidth: 120, fontSize: 22 }}
                                                    onClick={() => handleSelection("left", tipus.aidfinance_code)}
                                                    disabled={isLeftDisabled}
                                                >
                                                    {tipus.aidfinance_code}
                                                    {tipus.aidfinance_national && (
                                                        <span className="mt-1 text-secondary fs-6">
                                                            {tipus.aidfinance_national}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Jobb láb oszlop */}
                                    <div>
                                        <div className="fw-bold mb-2" style={{ fontSize: "2rem" }}>
                                            Jobb láb
                                        </div>
                                        <div className="d-flex flex-wrap gap-3 justify-content-center">
                                            {staticOptions?.shoes_med_types?.map((tipus) => (
                                                <button
                                                    key={"jobb-" + tipus.aidfinance_code}
                                                    type="button"
                                                    className={`btn w-100 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold ${
                                                        selectedRight === tipus.aidfinance_code
                                                            ? "btn-primary selected_button"
                                                            : "btn-outline-primary"
                                                    }`}
                                                    style={{ minWidth: 120, fontSize: 22 }}
                                                    onClick={() => handleSelection("right", tipus.aidfinance_code)}
                                                    disabled={isRightDisabled}
                                                >
                                                    {tipus.aidfinance_code}
                                                    {tipus.aidfinance_national && (
                                                        <span className="mt-1 text-secondary fs-6">
                                                            {tipus.aidfinance_national}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="d-flex justify-content-between mt-4">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                        <StepperNavButton
                            type="button"
                            className="btn btn-primary"
                            onClick={handleNext}
                            disabled={!canProceed || loading}
                        >
                            Tovább
                        </StepperNavButton>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={cancelSelection}
                onConfirm={confirmSelection}
                title="Módosítás megerősítése"
                confirmText="Módosítás"
                confirmClass="btn btn-primary" 
                cancelText="Mégsem"
                type="warning"
                icon={<IconExclamationMark size={70} stroke={2} className="text-warning" />}>
                <h4>Biztosan módosítani szeretné a már kiválasztott cipőtípust?</h4>
            </Modal>
        </TablerLayout>
    );
};

export default CipoTipus;
